import moment from 'moment';

/** Shared display / parse formats (moment tokens). */
export const MOMENT_FORMATS = {
  /** Partner API timestamps: "26-08-26 12:13:54" */
  PARTNER_API: 'YY-MM-DD HH:mm:ss',
  PARTNER_API_DATE: 'YY-MM-DD',
  DATE: 'MMM D, YYYY',
  DATE_SHORT: 'MMM D',
  DATE_WEEKDAY: 'ddd, MMM D, YYYY',
  DATE_WEEKDAY_SHORT: 'ddd, MMM D',
  DATE_LONG: 'dddd, D MMMM',
  DATE_LONG_FULL: 'dddd, DD MMMM',
  DATE_MONTH_LONG: 'MMMM D, YYYY',
  TIME: 'h:mm A',
  TIME_24: 'HH:mm',
  DATETIME: 'MMM D, YYYY h:mm A',
  DATETIME_LONG: 'MMMM D, YYYY h:mm A',
};

/**
 * Parse a value into a moment instance.
 * Auto-detects partner API `YY-MM-DD[ HH:mm:ss]` when no inputFormat is given.
 */
export const toMoment = (value, inputFormat) => {
  if (value == null || value === '') return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;

  if (inputFormat) {
    const parsed = moment(value, inputFormat, true);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (/^\d{2}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/.test(raw)) {
      const partner = moment(
        raw,
        [MOMENT_FORMATS.PARTNER_API, MOMENT_FORMATS.PARTNER_API_DATE],
        true,
      );
      if (partner.isValid()) return partner;
      return null;
    }

    // Avoid moment's Date() fallback warning for free-form garbage strings.
    if (!/^\d{4}-\d{2}-\d{2}|^\d{1,2}[/.-]\d{1,2}|T\d{2}:\d{2}|^\d+$/.test(raw)) {
      const known = moment(
        raw,
        [
          moment.ISO_8601,
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD HH:mm',
          'YYYY-MM-DD',
          'MMM D, YYYY',
          'MMMM D, YYYY',
        ],
        true,
      );
      if (known.isValid()) return known;
      return null;
    }
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

/** Format a date/time for display. Returns null when the value is missing/invalid. */
export const formatDate = (value, outputFormat = MOMENT_FORMATS.DATE, inputFormat) => {
  const parsed = toMoment(value, inputFormat);
  return parsed ? parsed.format(outputFormat) : null;
};

/** Format API `HH:mm` / `HH:mm:ss` (or Date) as "6:00 PM". */
export const formatTime = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date || moment.isMoment(value)) {
    return formatDate(value, MOMENT_FORMATS.TIME);
  }
  const raw = String(value).trim();
  return (
    formatDate(raw, MOMENT_FORMATS.TIME, 'HH:mm:ss') ||
    formatDate(raw, MOMENT_FORMATS.TIME, MOMENT_FORMATS.TIME_24) ||
    formatDate(raw, MOMENT_FORMATS.TIME, 'H:mm') ||
    formatDate(raw, MOMENT_FORMATS.TIME)
  );
};

/**
 * Parse event calendar date + optional start time (ISO or `YYYY-MM-DD` + `HH:mm`).
 * Returns a moment or null.
 */
export const parseEventDateTime = (eventDate, startTime) => {
  const dateRaw = String(eventDate ?? '').trim();
  const timeRaw = String(startTime ?? '').trim();
  if (!dateRaw) return null;

  if (dateRaw.includes('T')) {
    return toMoment(dateRaw);
  }

  if (timeRaw) {
    if (timeRaw.includes('T')) {
      const fromTime = toMoment(timeRaw);
      if (fromTime) return fromTime;
    }
    return (
      toMoment(`${dateRaw} ${timeRaw}`, 'YYYY-MM-DD HH:mm:ss') ||
      toMoment(`${dateRaw} ${timeRaw}`, 'YYYY-MM-DD HH:mm') ||
      toMoment(`${dateRaw} ${timeRaw}`)
    );
  }

  return toMoment(dateRaw, 'YYYY-MM-DD') || toMoment(dateRaw);
};

/** Moderation / case timestamps: "25 may 2025 5:30pm". */
export const formatModerationDateTime = (value) => {
  const label = formatDate(value, 'D MMMM YYYY h:mma');
  return label ? label.toLowerCase() : null;
};
